/**
 * UX-PROG/FE-4 (#16d3b07c) — reusable empty-state surface used across the
 * Programmazione editor. Wraps AntD `Empty` with a vertical-stack
 * description holding: title, body, optional helper text, and one to two
 * CTAs (primary + optional secondary). AntD primitives only.
 *
 * Applied at four sites today:
 *   (a) ProgrammazioneForm — no splits yet
 *   (b) ProgrammazioneForm — split with no exercises
 *   (c) WeeksTabContent  — exercise with no weeks (post-SETS-REMOVE/C wiring)
 *   (d) EditProgrammazione/create — brand-new programmazione (variant of (a))
 */
import { Button, Empty, Flex } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

export type EmptyStateCta = {
  label: string;
  onClick: () => void;
  icon?: IconDefinition;
};

export type EmptyStateCardProps = {
  icon?: IconDefinition;
  title: string;
  body: string;
  primaryCta: EmptyStateCta;
  secondaryCta?: EmptyStateCta;
  helperText?: string;
};

export default function EmptyStateCard({
  icon,
  title,
  body,
  primaryCta,
  secondaryCta,
  helperText,
}: EmptyStateCardProps) {
  return (
    <Empty
      image={
        icon ? (
          <FontAwesomeIcon icon={icon} style={{ fontSize: 32, color: "var(--app-gray-400)" }} />
        ) : (
          Empty.PRESENTED_IMAGE_SIMPLE
        )
      }
      description={
        <Flex vertical gap={8} align="center" style={{ paddingTop: 8 }}>
          <strong style={{ fontSize: 14 }}>{title}</strong>
          <span style={{ color: "var(--app-gray-600)", fontSize: 13, maxWidth: 420, textAlign: "center" }}>
            {body}
          </span>
          {helperText && (
            <span style={{ color: "var(--app-gray-500)", fontSize: 12, fontStyle: "italic", maxWidth: 420, textAlign: "center" }}>
              {helperText}
            </span>
          )}
          <Flex gap={8} wrap="wrap" justify="center" style={{ marginTop: 4 }}>
            <Button
              type="primary"
              onClick={primaryCta.onClick}
              icon={primaryCta.icon ? <FontAwesomeIcon icon={primaryCta.icon} /> : undefined}
            >
              {primaryCta.label}
            </Button>
            {secondaryCta && (
              <Button
                onClick={secondaryCta.onClick}
                icon={secondaryCta.icon ? <FontAwesomeIcon icon={secondaryCta.icon} /> : undefined}
              >
                {secondaryCta.label}
              </Button>
            )}
          </Flex>
        </Flex>
      }
    />
  );
}
